import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState('dark');

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');
      
      if (storedUser && storedToken && storedToken !== 'undefined' && storedUser !== 'undefined') {
        const userData = JSON.parse(storedUser);
        if (userData && userData.id && userData.email) {
          setUser(userData);
          setTheme(userData.theme || 'dark');
        } else {
          // Invalid user data, clear it
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      } else {
        // Missing or invalid data, clean up
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    } catch (err) {
      console.error('[Auth] Error restoring session:', err);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      const data = response.data;
      
      // Handle old backend with 2FA still active - skip 2FA and retry
      if (data.requires_2fa) {
        const err = new Error('Backend version mismatch. Please redeploy backend.');
        err.code = 'VERSION_MISMATCH';
        throw err;
      }
      
      // Validate response has required fields
      if (!data.access_token || !data.user) {
        const err = new Error('Invalid server response format');
        err.code = 'INVALID_RESPONSE';
        throw err;
      }
      
      const { access_token, user: userData } = data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      setTheme(userData.theme || 'dark');
      
      return userData;
    } catch (error) {
      // Clear any stale auth data on login failure
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      throw error;
    }
  };

  const register = async (email, password, fullName) => {
    const response = await authAPI.register({ 
      email, 
      password, 
      full_name: fullName 
    });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setTheme(userData.theme || 'dark');
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const toggleTheme = async () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    
    if (user) {
      try {
        await authAPI.updateTheme(newTheme);
        const updatedUser = { ...user, theme: newTheme };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      } catch (error) {
        console.error('Failed to update theme:', error);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      theme, 
      toggleTheme,
      isAuthenticated: !!user,
      isAdmin: user?.role === 'admin',
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
